/**
 * `can()` — the client half of the authorisation model.
 *
 * There is no role logic here, and that is the point. The server sends
 * *permissions*, already resolved through `ROLE_PERMISSIONS`, so this file
 * is a lookup and nothing else. Re-deriving "a creator may submit" in
 * TypeScript would be a second source of truth for the authorisation
 * table, and the first place it drifts is a button that offers an action
 * the server then refuses.
 *
 * **This is cosmetic.** Hiding a nav item is a courtesy, not a control —
 * every route re-checks server-side, and the Playwright suite navigates
 * directly to a hidden route and asserts a 403. If this file were wrong in
 * the permissive direction the only cost is a button that errors; if the
 * server were wrong, that is an incident.
 */

import type { MePermissions } from "@/features/auth/api";

/**
 * Permission strings, mirroring `mdm.auth.permissions.Permission`.
 *
 * A union rather than free-form strings so a typo is a compile error. It
 * is not generated from the OpenAPI document because the document types
 * these as plain `string[]` — the enum is not in the schema, only in the
 * values. Worth a follow-up; a typo here fails closed (nothing matches),
 * which is the safe direction.
 */
export type Permission =
  | "user.read"
  | "user.provision"
  | "user.activate"
  | "user.deactivate"
  | "role.grant"
  | "role.revoke"
  | "tenant.read"
  | "tenant.update"
  | "master_type.read"
  | "master_type.define"
  | "schema.read"
  | "schema.define"
  | "schema.submit"
  | "schema.approve"
  | "record.read"
  | "record.create"
  | "record.edit"
  | "record.submit"
  | "record.approve"
  | "record.deactivate"
  | "reviewer.assign"
  | "handover.request"
  | "handover.decide"
  | "template.download"
  | "import.run"
  | "export.run"
  | "audit.read.master_data"
  | "audit.read.user_management";

/**
 * Does the caller hold `permission`, optionally at one master type?
 *
 * The same rule `mdm.auth.authorization._satisfies` applies: a global
 * grant satisfies any scope, a scoped grant satisfies only its own. Both
 * halves are consulted because the server does not duplicate global
 * permissions into every type's list.
 *
 * Called with no `typeCode`, this asks "globally" — deliberately strict.
 * "May I create a record *somewhere*" is a different question, and
 * `canAnywhere` below is the one that answers it.
 */
export function can(
  permissions: MePermissions | undefined,
  permission: Permission,
  typeCode?: string,
): boolean {
  if (!permissions) return false;
  if (permissions.global.includes(permission)) return true;
  if (typeCode === undefined) return false;
  return permissions.by_master_type[typeCode]?.includes(permission) ?? false;
}

/**
 * Does the caller hold `permission` at *any* scope?
 *
 * What a nav item actually needs: a creator scoped to Country alone
 * should still see the Records section — hiding it would leave them with
 * no route to the one type they can work on. Which types they may act on
 * is the list page's job, not the nav's.
 */
export function canAnywhere(
  permissions: MePermissions | undefined,
  permission: Permission,
): boolean {
  if (!permissions) return false;
  if (permissions.global.includes(permission)) return true;
  return Object.values(permissions.by_master_type).some((held) => held.includes(permission));
}

/** Master type codes the caller holds `permission` at, plus the global case. */
export function scopesFor(
  permissions: MePermissions | undefined,
  permission: Permission,
): { global: boolean; codes: string[] } {
  if (!permissions) return { global: false, codes: [] };
  return {
    global: permissions.global.includes(permission),
    codes: Object.entries(permissions.by_master_type)
      .filter(([, held]) => held.includes(permission))
      .map(([code]) => code)
      .sort(),
  };
}
